import { useEffect, useRef } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { ensurePushSubscriptionForUser, isPushNotificationSupported } from '@/services/pushNotificationService';
import {
  NOTIFICATION_FIELD_MAPPINGS,
  NOTIFICATION_TYPE_OPTIONS,
  type NotificationPreferenceKey,
} from '@/data/notificationPreferences';
import { isRunningStandalonePwa } from '@/utils/pwa';

export function PwaPushNotificationBootstrapper(): null {
  const { currentUser } = useAuth();
  const lastHandledUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const userId = currentUser?.id ?? null;

    if (!userId) {
      lastHandledUserIdRef.current = null;
      return;
    }

    if (lastHandledUserIdRef.current === userId) {
      return;
    }

    lastHandledUserIdRef.current = userId;

    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return;
    }

    if (!isRunningStandalonePwa()) {
      return;
    }

    if (!isPushNotificationSupported()) {
      return;
    }

    let cancelled = false;

    const ensurePreferencesEnabled = async () => {
      const { data, error } = await supabase
        .from('user_push_notifications')
        .select('user_id')
        .eq('user_id', userId)
        .maybeSingle();

      if (cancelled) {
        return;
      }

      if (error) {
        console.warn('No se pudo comprobar el registro de notificaciones push del usuario:', error);
        return;
      }

      if (data) {
        return;
      }

      if (Notification.permission === 'denied') {
        return;
      }

      const permission = Notification.permission === 'granted'
        ? 'granted'
        : await Notification.requestPermission();

      if (cancelled || permission !== 'granted') {
        return;
      }

      try {
        await ensurePushSubscriptionForUser(userId);
      } catch (subscriptionError) {
        console.warn('No se pudo registrar la suscripción push del usuario automáticamente:', subscriptionError);
        return;
      }

      if (cancelled) {
        return;
      }

      const disallowedPreferenceKeys = new Set<NotificationPreferenceKey>(['notiEmail']);

      const preferenceUpdates: Record<string, boolean> = {
        [NOTIFICATION_FIELD_MAPPINGS.notiPush]: true,
      };

      NOTIFICATION_TYPE_OPTIONS.forEach((option) => {
        if (disallowedPreferenceKeys.has(option.key)) {
          return;
        }

        const fieldName = NOTIFICATION_FIELD_MAPPINGS[option.key];
        preferenceUpdates[fieldName] = true;
      });

      const { error: preferenceError } = await supabase
        .from('users')
        .update(preferenceUpdates)
        .eq('id', userId);

      if (preferenceError) {
        console.warn('No se pudieron actualizar las preferencias de notificaciones tras habilitar push:', preferenceError);
      }
    };

    ensurePreferencesEnabled().catch((error) => {
      console.warn('No se pudieron activar automáticamente las notificaciones push del usuario:', error);
    });

    return () => {
      cancelled = true;
    };
  }, [currentUser?.id]);

  return null;
}

export default PwaPushNotificationBootstrapper;
